import React, { useEffect, useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CreditTracker } from "@/components/CreditTracker";
import { Award, BookOpen, Calendar, AlertTriangle, CheckCircle2, CreditCard, ExternalLink, MessageCircle, Send, Clock, FileText } from "lucide-react";

const EXAM_BADGE = {
  passed: ["bg-emerald-100 text-emerald-700", CheckCircle2],
  needs_repayment: ["bg-red-100 text-red-700", CreditCard],
  failed_resit: ["bg-amber-100 text-amber-700", AlertTriangle],
  awaiting_grading: ["bg-blue-100 text-blue-700", Clock],
  in_progress: ["bg-amber-100 text-amber-700", Clock],
  not_started: ["bg-[var(--muted-surface)] text-[var(--navy)]", FileText],
};

function examStatusText(exam) {
  if (!exam) return "Live programme";
  const left = exam.resits_left;
  return {
    passed: `Completed · PASS (final)${exam.best_percent != null ? ` · ${exam.best_percent}%` : ""}`,
    needs_repayment: "Not yet passed · pay to resit",
    failed_resit: `Not yet passed · ${left} resit${left === 1 ? "" : "s"} left`,
    awaiting_grading: "Submitted · awaiting grading",
    in_progress: "Exam in progress",
    not_started: "Exam not started",
  }[exam.status];
}

const downloadPdf = async (path, fname) => {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = fname; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("Download failed"); }
};

function ResultDownloads({ course }) {
  const code = course.code || "exam";
  return (
    <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-[var(--border-warm)]">
      <button onClick={() => downloadPdf(`/courses/${course.id}/my-results-report`, `csi-results-report-${code}.pdf`)} className="chip text-xs inline-flex items-center gap-1" data-testid={`dash-report-${course.id}`}>
        <FileText size={12}/> Results report
      </button>
      <button onClick={() => downloadPdf(`/courses/${course.id}/my-transcript`, `csi-transcript-${code}.pdf`)} className="chip text-xs inline-flex items-center gap-1" data-testid={`dash-transcript-${course.id}`}>
        <FileText size={12}/> Transcript
      </button>
    </div>
  );
}

function ExamBadge({ exam }) {
  const [cls, Icon] = EXAM_BADGE[exam?.status] || ["bg-[var(--muted-surface)] text-[var(--navy)]", BookOpen];
  return <div className={`w-14 h-14 shrink-0 rounded-full grid place-items-center ${cls}`}><Icon size={22}/></div>;
}

const CPD_STATUS = {
  complete: ["bg-emerald-100 text-emerald-800", "CPD complete"],
  on_track: ["bg-sky-100 text-sky-800", "CPD on track"],
  at_risk: ["bg-amber-100 text-amber-800", "CPD due soon"],
  overdue: ["bg-red-100 text-red-700", "CPD overdue"],
};

const CpdStatus = ({ cycle }) => {
  const [cls, label] = CPD_STATUS[cycle.status] || CPD_STATUS.on_track;
  return (
    <div className="mt-3 pt-3 border-t border-[var(--border-warm)]" data-testid={`cpd-status-${cycle.designation_code}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${cls}`} data-testid={`cpd-status-badge-${cycle.designation_code}`}>{label}</span>
        <span className="text-xs text-zinc-600">{cycle.logged_hours}/{cycle.required_hours} h logged</span>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-zinc-200 overflow-hidden"><div className="h-full bg-[var(--navy)]" style={{ width: `${cycle.progress_percent}%` }}/></div>
      <div className="text-xs text-zinc-600 mt-2" data-testid={`cpd-due-${cycle.designation_code}`}>
        Next CPD declaration due <b>{new Date(cycle.cycle_end).toLocaleDateString("en-GB")}</b>
        {cycle.days_remaining >= 0 ? ` · ${cycle.days_remaining} days left` : ` · ${-cycle.days_remaining} days overdue`}
      </div>
      <div className="flex flex-wrap gap-3 mt-2 text-xs font-semibold">
        <Link to="/cpd" className="text-[var(--terracotta)] hover:underline" data-testid={`cpd-log-${cycle.designation_code}`}>Log CPD</Link>
        <a href="https://gcsb.uk/cpd" target="_blank" rel="noopener noreferrer" className="text-[var(--terracotta)] hover:underline" data-testid={`cpd-submit-${cycle.designation_code}`}>Declare at GCSB ↗</a>
      </div>
    </div>
  );
};

export default function DashboardPage() {
  useSeo({ title: "My dashboard", path: "/dashboard", noindex: true });
  const { user } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [certs, setCerts] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [cpd, setCpd] = useState(null);
  const [intents, setIntents] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");

  const loadEnquiries = () => api.get("/enquiries/me").then((r) => setEnquiries(r.data || [])).catch(() => {});

  useEffect(() => {
    api.get("/enrollments/me").then((r) => setEnrollments(r.data));
    api.get("/certificates/me").then((r) => setCerts(r.data));
    api.get("/live-sessions/upcoming/me").then((r) => setUpcoming(r.data));
    api.get("/cpd/summary").then((r) => setCpd(r.data)).catch(() => {});
    api.get("/payments/intents/me").then((r) => setIntents(r.data || [])).catch(() => {});
    loadEnquiries();
  }, []);

  const attention = (cpd?.cycles || []).filter((c) => c.status === "at_risk" || c.status === "overdue");
  const pending = intents.filter((i) => i.status === "awaiting_payment" || i.status === "pending_review");
  const passedEnrols = enrollments.filter((e) => e.exam?.status === "passed");

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="eyebrow mb-2">Welcome back</div>
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <h1 className="font-display text-3xl sm:text-5xl leading-tight">Hi {user?.name?.split(" ")[0]}, let's keep learning.</h1>
        <div className="flex gap-2">
          <Link to="/ambassador" className="chip text-xs inline-flex items-center gap-1" data-testid="dashboard-ambassador-link"><Award size={12}/> Ambassador</Link>
          <Link to="/account" className="chip text-xs" data-testid="dashboard-account-link">My account &amp; password</Link>
        </div>
      </div>

      {attention.length > 0 && (
        <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-5 flex items-start gap-4" data-testid="cpd-renewal-banner">
          <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={22}/>
          <div className="flex-1">
            <div className="font-display text-xl text-amber-900">CPD renewal reminder</div>
            <p className="text-sm text-amber-800 mt-1">
              {attention.map((a) => `${a.designation_code}: ${a.remaining_hours}h still needed, cycle ends in ${a.days_remaining} days`).join(" · ")}
            </p>
          </div>
          <Link to="/cpd" className="btn-ink px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap" data-testid="cpd-banner-cta">Log activity</Link>
        </div>
      )}

      {pending.length > 0 && (
        <div className="mb-6 rounded-2xl border border-[var(--border-warm)] bg-[var(--muted-surface)] p-5 space-y-3" data-testid="pending-intents-banner">
          <div className="flex items-start gap-3">
            <CreditCard className="text-[var(--terracotta)] shrink-0 mt-0.5" size={22}/>
            <div className="flex-1">
              <div className="font-display text-xl text-[var(--navy)]">Payments waiting on you</div>
              <p className="text-sm text-zinc-700 mt-1">
                You have {pending.length} enrolment{pending.length === 1 ? "" : "s"} waiting for confirmation. Already paid? Add your SumUp reference or a photo of your receipt below.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-2">
            {pending.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-3 flex-wrap rounded-xl bg-white border border-[var(--border-warm)] px-4 py-3" data-testid={`pending-intent-${p.id}`}>
                <div className="min-w-0 flex-1 basis-full sm:basis-auto">
                  <div className="font-semibold text-sm truncate">{p.course_title}{p.tier_label ? ` · ${p.tier_label}` : ""}</div>
                  <div className="text-[11px] text-zinc-500">€{p.amount} · {p.status === "awaiting_payment" ? "Awaiting your payment confirmation" : "Pending CSI review"}</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {p.payment_link_url && (
                    <a href={p.payment_link_url} target="_blank" rel="noreferrer" className="chip text-xs inline-flex items-center gap-1" data-testid={`pending-pay-${p.id}`}>
                      <ExternalLink size={12}/> Reopen SumUp
                    </a>
                  )}
                  <Link to={`/courses/${p.course_id}?confirm=${p.id}`} className="btn-terracotta inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold" data-testid={`pending-confirm-${p.id}`}>
                    I paid - add proof
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 [&>section]:min-w-0">
        {user?.role === "student" && <CreditTracker/>}
        <section className="lg:col-span-8 card-warm p-5 sm:p-6">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <h2 className="font-display text-2xl inline-flex items-center gap-2"><BookOpen size={18}/> My courses</h2>
            <Link to="/courses" className="chip">Browse more</Link>
          </div>
          {enrollments.length === 0 ? (
            <div className="py-10 text-center text-zinc-500">You haven't enrolled yet. <Link to="/courses" className="underline">Find a course</Link>.</div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {enrollments.map((e) => (
                <div key={e.id} className="card-warm p-4 transition-transform hover:-translate-y-0.5" data-testid={`enrolled-${e.course.id}`}>
                  <Link to={`/courses/${e.course.slug || e.course.id}`} className="flex items-center gap-4">
                    <ExamBadge exam={e.exam}/>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm break-words">{e.course.title}</div>
                      <div className="text-xs text-zinc-500" data-testid={`enrolled-status-${e.course.id}`}>{examStatusText(e.exam)}</div>
                    </div>
                  </Link>
                  {e.exam?.status === "passed" && <ResultDownloads course={e.course}/>}
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="lg:col-span-4 card-warm p-5 sm:p-6">
          <h2 className="font-display text-2xl inline-flex items-center gap-2 mb-4"><Calendar size={18}/> Upcoming</h2>
          {upcoming.length === 0 ? (
            <div className="text-sm text-zinc-500">No sessions scheduled.</div>
          ) : (
            <div className="space-y-3">
              {upcoming.map((s) => (
                <div key={s.id} className="border border-[var(--border-warm)] rounded-xl p-3">
                  <div className="text-xs text-zinc-500">{new Date(s.scheduled_at).toLocaleString()}</div>
                  <div className="font-semibold text-sm break-words">{s.title}</div>
                  <a href={s.meeting_url} target="_blank" rel="noreferrer" className="text-xs text-[var(--terracotta)] font-bold">Join link →</a>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="lg:col-span-12 card-warm p-5 sm:p-6">
          <h2 className="font-display text-2xl inline-flex items-center gap-2 mb-2"><Award size={18}/> Certificates</h2>
          <p className="text-sm text-zinc-600 mb-4">Certificates are sent by email from the CSI Admin team once you pass your examination.</p>
          {passedEnrols.length === 0 && certs.length === 0 ? (
            <div className="text-sm text-zinc-500" data-testid="certificates-empty">No passed examinations yet.</div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {passedEnrols.map((e) => (
                <div key={e.id} className="border border-[var(--border-warm)] rounded-2xl p-5 bg-gradient-to-br from-[var(--muted-surface)] to-white" data-testid={`cert-status-${e.course.id}`}>
                  <div className="eyebrow mb-1">PASS (final){e.exam.best_percent != null ? ` · ${e.exam.best_percent}%` : ""}</div>
                  <div className="font-display text-xl leading-tight">{e.course.title}</div>
                  <div className={`mt-3 text-sm ${e.certificate_sent_at ? "text-emerald-700" : "text-zinc-600"}`}>
                    {e.certificate_sent_at ? `Certificate sent by email on ${new Date(e.certificate_sent_at).toLocaleDateString()} - please also check your spam / junk folders` : "Your certificate will be sent by the CSI Administrator by separate email within 24 hours"}
                  </div>
                  {(() => {
                    const cycle = e.certificate_sent_at && (cpd?.cycles || []).find((c) => c.designation_code === (e.course.code || "").split("-")[0]);
                    return cycle ? <CpdStatus cycle={cycle}/> : null;
                  })()}

                </div>
              ))}
              {certs.map((c) => (
                <div key={c.id} className="relative border border-[var(--border-warm)] rounded-2xl p-5 bg-gradient-to-br from-[var(--muted-surface)] to-white" data-testid={`cert-${c.id}`}>
                  <div className="eyebrow mb-1">Certificate · {c.id}</div>
                  <div className="font-display text-xl leading-tight">{c.course_title}</div>
                  <div className="mt-3 text-sm">Awarded to <b>{c.user_name}</b></div>
                  <div className="text-xs text-zinc-500">Score {c.score}% · {new Date(c.issued_at).toLocaleDateString()}</div>
                  <div className="absolute top-3 right-3 w-10 h-10 rounded-full bg-[var(--terracotta)] text-white grid place-items-center font-display text-[10px]">CSI</div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="lg:col-span-12 card-warm p-5 sm:p-6" data-testid="dashboard-enquiries">
          <h2 className="font-display text-2xl mb-4 inline-flex items-center gap-2"><MessageCircle size={18}/> My enquiries</h2>
          {enquiries.length === 0 ? (
            <p className="text-sm text-zinc-500">No enquiries yet. Open any programme page and use the <b>Contact instructor &amp; admin</b> form to send one.</p>
          ) : (
            <div className={`grid grid-cols-1 gap-4 ${enquiries.length > 1 ? "xl:grid-cols-2" : ""}`}>
              {enquiries.map((e) => (
                <div key={e.id} className="rounded-2xl border border-[var(--border-warm)] bg-white p-4 sm:p-5 min-w-0" data-testid={`enquiry-${e.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="eyebrow">{e.course_title}</div>
                      <h3 className="font-display text-lg break-words">{e.subject}</h3>
                      <p className="text-[11px] text-zinc-500 mt-0.5">Opened {new Date(e.created_at).toLocaleString()} · {e.messages?.length || 0} message{(e.messages?.length || 0) === 1 ? "" : "s"}</p>
                    </div>
                    <span className={`shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${e.status === "open" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>{e.status}</span>
                  </div>
                  <ul className="mt-4 space-y-2 max-h-80 overflow-y-auto pr-1">
                    {(e.messages || []).map((m) => (
                      <li key={m.id} className={`rounded-xl p-3 ${m.role === "student" ? "bg-[var(--muted-surface)] sm:mr-8" : "bg-[var(--navy)]/5 border-l-2 border-[var(--terracotta)] sm:ml-8"}`}>
                        <div className="text-[11px] text-zinc-500 mb-1"><b>{m.by_name}</b> · {m.role} · {new Date(m.at).toLocaleString()}</div>
                        <div className="text-sm text-zinc-800 whitespace-pre-wrap break-words">{m.text}</div>
                      </li>
                    ))}
                  </ul>
                  {replyTo === e.id ? (
                    <form
                      className="mt-3 flex gap-2 flex-wrap"
                      onSubmit={async (ev) => {
                        ev.preventDefault();
                        if (!replyText.trim()) return;
                        try {
                          await api.post(`/enquiries/${e.id}/reply`, { text: replyText.trim() });
                          toast.success("Reply sent");
                          setReplyText(""); setReplyTo(null);
                          loadEnquiries();
                        } catch (err) { toast.error(err.response?.data?.detail || "Reply failed"); }
                      }}
                    >
                      <input
                        value={replyText}
                        onChange={(ev) => setReplyText(ev.target.value)}
                        placeholder="Your reply…"
                        className="flex-1 min-w-0 basis-full sm:basis-auto p-2 border border-[var(--border-warm)] rounded-lg text-sm"
                        autoFocus
                        data-testid={`enquiry-reply-input-${e.id}`}
                      />
                      <button type="submit" className="btn-terracotta inline-flex items-center gap-1 px-3 py-2 rounded-full text-sm" data-testid={`enquiry-reply-send-${e.id}`}><Send size={13}/> Send</button>
                      <button type="button" onClick={() => { setReplyTo(null); setReplyText(""); }} className="chip text-sm">Cancel</button>
                    </form>
                  ) : (
                    <button onClick={() => setReplyTo(e.id)} className="chip mt-3 text-xs inline-flex items-center gap-1" data-testid={`enquiry-reply-${e.id}`}>
                      <Send size={12}/> Reply
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
